import React, { useEffect, useRef, useState } from 'react'
import { Button, Card, Form, Modal } from 'react-bootstrap'
import { useSelector } from 'react-redux'
import { Link, Route, withRouter } from 'react-router-dom'

import cogoToast from 'cogo-toast'
import { orderBy } from 'lodash'
import Moment from 'moment'

import createFormHelpers from '../../FormHelpers'
import CasesService from '../../services/CasesService'
import UserService from '../../services/UserService'
import { ROLE_NAMES } from '../../utils/Constants'
import { getToken } from '../../utils/getToken'
import Permissions, { CAPABILITIES } from '../../utils/Permissions'
import AppointmentDashboardCard from '../AppointmentDashboardCard'
import CaseSummary from '../CaseSummary'
import CaseCloseButton from '../controls/CaseCloseButton'
import TextArea from '../controls/TextArea'
import FAIcon from '../FAIcon'
import LoadingSpinner from '../LoadingSpinner'
import TaskCard from '../TaskCard'

import ReportsListPage from './CaseReport/ReportsListPage'
import { applyAcceptedProviders, HitlReviewModal, sanitizeCaseSystemsAndFunctional } from './HitlReview'
import KeyContactsCard from './KeyContactsCard'
import ManageCaseMembersModal from './ManageCaseMembersModal'
import { SmartCaseDetailsReviewModal } from './SmartCaseReview'

import './OverviewPage.scss'

const formatDate = date => {
  return !!date && date !== '--' ? Moment(date).format('MM/DD/YYYY') : '--'
}

const OverviewPage = withRouter(
  ({
    caseNumber,
    incompleteAppointments,
    allAppointments,
    incompleteTasks,
    settings,
    showError,
    history,
    match,
    completeTasks,
    user,
    refreshCaseStatus,
    onTaskDeleted,
    location,
    isDraft,
  }) => {
    const { getValue } = createFormHelpers({})
    const reduxUser = useSelector(state => state?.currentUser)
    const effectiveUser = user || reduxUser
    const [caseModel, setCaseModel] = useState()
    const [showAllAppointments, setShowAllAppointments] = useState(false)
    const [caseSummaryModel, setCaseSummaryModel] = useState()
    const [isLoading, setIsLoading] = useState(true)
    const [users, setUsers] = useState([])
    const clientFirstName = getValue('client.firstName', '', caseModel)
    const clientLastName = getValue('client.lastName', '', caseModel)
    const injuryDateValue = formatDate(getValue('injury.date', '', caseModel))
    const birthDateValue = formatDate(getValue('client.birthdate', '', caseModel))
    const claimNumberValue = getValue('referral.claimNumber', '', caseModel)
    const [firstName, setFirstName] = useState(clientFirstName)
    const [lastName, setLastName] = useState(clientLastName)
    const [caseMembers, setCaseMembers] = useState([])
    const [isEditing, setIsEditing] = useState(false)
    const [isEditingInjuryDate, setIsEditingInjuryDate] = useState(false)
    const [isEditingBirthdate, setIsEditingBirthdate] = useState(false)
    const [injuryDate, setInjuryDate] = useState('')
    const [birthDate, setBirthDate] = useState('')
    const [isEditingClaimNumber, setIsEditingClaimNumber] = useState(false)
    const [claimNumber, setClaimNumber] = useState('')
    const inputRef = useRef(null)
    const [showMembersModal, setShowMembersModal] = useState(false)
    const [showCloseModal, setShowCloseModal] = useState(false)
    const [isActivating, setIsActivating] = useState(false)
    const [activateError, setActivateError] = useState(null)
    const [showDeleteDraftConfirm, setShowDeleteDraftConfirm] = useState(false)
    const [isDeletingDraft, setIsDeletingDraft] = useState(false)
    const [isGeneratingLink, setIsGeneratingLink] = useState(false)
    const [reviewerLinkToast, setReviewerLinkToast] = useState(null)
    const [isDownloadingStatusReport, setIsDownloadingStatusReport] = useState(false)
    const [isRefreshingMetadata, setIsRefreshingMetadata] = useState(false)
    const [showReferralModal, setShowReferralModal] = useState(false)
    const [referralNotesText, setReferralNotesText] = useState('')
    const [isSavingReferral, setIsSavingReferral] = useState(false)
    const [showHitlModal, setShowHitlModal] = useState(false)
    const [showHygieneModal, setShowHygieneModal] = useState(false)

    const canUpdateCase = Permissions.can(effectiveUser, CAPABILITIES.UPDATE_CASE)
    const isUserAdmin =
      (effectiveUser &&
        effectiveUser.roles &&
        (effectiveUser.roles.includes('admin') || effectiveUser.roles.includes('case-admin'))) ||
      canUpdateCase
    const [showSettingsModal, setShowSettingsModal] = useState(false)
    const [skipInitialReport, setSkipInitialReport] = useState(false)
    const [firstProgressSeq, setFirstProgressSeq] = useState('')
    const [firstStartDate, setFirstStartDate] = useState('')
    const [isSavingSettings, setIsSavingSettings] = useState(false)

    const handleOpenSettings = () => {
      setSkipInitialReport(!!caseModel?.skipInitialReport)
      setFirstProgressSeq(caseModel?.firstProgressReportSequenceNumber ?? '1')
      setFirstStartDate(
        caseModel?.firstReportStartingDate ? Moment(caseModel.firstReportStartingDate).format('YYYY-MM-DD') : '',
      )
      setShowSettingsModal(true)
    }

    const handleSaveSettings = async e => {
      if (e) e.preventDefault()
      if (isSavingSettings) return
      setIsSavingSettings(true)
      try {
        const data = {
          skipInitialReport,
          firstProgressReportSequenceNumber: parseInt(firstProgressSeq, 10) || 1,
          firstReportStartingDate: firstStartDate ? new Date(firstStartDate).toISOString() : null,
        }
        await CasesService.updateReportingSettings(caseNumber, data)
        setCaseModel(prev => ({
          ...prev,
          skipInitialReport: data.skipInitialReport,
          firstProgressReportSequenceNumber: data.firstProgressReportSequenceNumber,
          firstReportStartingDate: data.firstReportStartingDate,
        }))
        setShowSettingsModal(false)
        cogoToast.success('Reporting settings updated successfully')
      } catch (err) {
        showError(err.message || 'Failed to update reporting settings')
      } finally {
        setIsSavingSettings(false)
      }
    }

    const handleSaveReferral = async e => {
      if (e) e.preventDefault()
      if (isSavingReferral) return
      setIsSavingReferral(true)
      try {
        const updated = { ...caseModel, detailsForCaseManager: referralNotesText }
        await CasesService.update(updated)
        setCaseModel(updated)
        setShowReferralModal(false)
        cogoToast.success('Referral Request updated successfully')
      } catch (err) {
        showError(err.message || 'Failed to update Referral Request')
      } finally {
        setIsSavingReferral(false)
      }
    }

    const handleEntered = () => {
      const modalBody = document.querySelector('.modal-body')
      if (modalBody) {
        const firstInput = modalBody.querySelector('textarea, input')
        if (firstInput) firstInput.focus()
      }
    }

    useEffect(() => {
      const casePromise = CasesService.get(caseNumber).then(caseObj => {
        setCaseModel(caseObj)
        setCaseMembers(caseObj.caseMembers)
        return caseObj
      })

      const caseSummaryModel = CasesService.getSummary(caseNumber).then(r => {
        setCaseSummaryModel(r)
      })

      if (refreshCaseStatus) {
        refreshCaseStatus()
      }

      Promise.all([casePromise, caseSummaryModel])
        .then(() => {
          setIsLoading(false)
        })
        .catch(err => {
          showError(err.message)
          setIsLoading(false)
        })
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [caseNumber, settings])

    useEffect(() => {
      if (Permissions.can(effectiveUser, CAPABILITIES.MANAGE_JOBS)) {
        ;(async () => {
          try {
            const users = await UserService.getAll()
            setUsers(users)
          } catch {}
        })()
      }
    }, [setUsers, effectiveUser])

    useEffect(() => {
      const handleNotification = e => {
        const notif = e.detail
        if (
          (notif.title === 'Refresh Complete' ||
            notif.title === 'Smart Upload Complete' ||
            notif.title === 'Smart Intake Complete' ||
            notif.title === 'Smart Document Review Complete') &&
          notif.deepLink?.includes(`/cases/${caseNumber}`)
        ) {
          CasesService.get(caseNumber).then(updatedCase => {
            setCaseModel(updatedCase)
            if (updatedCase?.pendingExtractions || updatedCase?.extensions?.pendingExtractions) {
              setShowHitlModal(true)
            }
          })
          CasesService.getSummary(caseNumber).then(setCaseSummaryModel)
          if (refreshCaseStatus) refreshCaseStatus()
          setIsRefreshingMetadata(false)
        }
      }

      window.addEventListener('app-notification', handleNotification)
      return () => {
        window.removeEventListener('app-notification', handleNotification)
      }
    }, [caseNumber, refreshCaseStatus])

    useEffect(() => {
      const searchParams = new URLSearchParams(location?.search)
      if (searchParams.get('tab') === 'documents') {
        const remaining = location.search
          ? location.search.replace(/[?&]tab=documents(&|$)/, '$1').replace(/^&/, '?')
          : ''
        history.replace(`/cases/${caseNumber}/documents${remaining}`)
        return
      }
      if (searchParams.get('review') === 'true' || searchParams.get('from') === 'intake') {
        setShowHitlModal(true)
        searchParams.delete('review')
        searchParams.delete('from')
        const remaining = searchParams.toString()
        history.replace(`${location.pathname}${remaining ? `?${remaining}` : ''}`)
      }
    }, [location?.search, caseNumber, history, location?.pathname])

    useEffect(() => {
      setFirstName(clientFirstName)
      setLastName(clientLastName)
      setInjuryDate(injuryDateValue)
    }, [clientFirstName, setFirstName, clientLastName, setLastName, injuryDateValue, setInjuryDate])

    if (isLoading) return <LoadingSpinner />
    if (!caseModel) return null

    const refreshMembers = async () => {
      const updatedCase = await CasesService.get(caseNumber)
      setCaseModel(updatedCase)
      setCaseMembers(updatedCase.caseMembers)
    }

    const handleApplySmartIntakeHitlChanges = async acceptedEntities => {
      if (!caseModel) return
      const cleanCombinedVal = val => {
        if (!val || typeof val !== 'string') return val
        return val.replace(/\s+(?:\||I)\s+[A-Za-z][A-Za-z0-9\s#/()]{1,28}\s*:.*$/i, '').trim()
      }
      let updatedCase = {
        ...caseModel,
        referral: caseModel.referral
          ? {
              ...caseModel.referral,
              claimNumber: cleanCombinedVal(caseModel.referral.claimNumber),
              policyNumber: cleanCombinedVal(caseModel.referral.policyNumber),
            }
          : caseModel.referral,
        client: caseModel.client
          ? {
              ...caseModel.client,
              firstName: cleanCombinedVal(caseModel.client.firstName || '').replace(
                /^(?:RN|LPN|MD|DO|PT|DPT|DC|NP|PA-?C|CCM|CRC)\b[\s,.-]*/i,
                '',
              ),
              lastName: cleanCombinedVal(caseModel.client.lastName || ''),
              phone: cleanCombinedVal(caseModel.client.phone),
            }
          : caseModel.client,
        employer: caseModel.employer
          ? {
              ...caseModel.employer,
              occupation: cleanCombinedVal(caseModel.employer.occupation),
            }
          : caseModel.employer,
      }
      let occupationExplicitlyUpdated = false
      acceptedEntities.forEach(ent => {
        if (ent.field === 'Claim Number') {
          updatedCase = {
            ...updatedCase,
            referral: { ...(updatedCase.referral || {}), claimNumber: cleanCombinedVal(ent.extractedValue) },
          }
        } else if (ent.field === 'Policy Number') {
          updatedCase = {
            ...updatedCase,
            referral: { ...(updatedCase.referral || {}), policyNumber: cleanCombinedVal(ent.extractedValue) },
          }
        } else if (ent.field === 'Date of Referral') {
          let refDate = ent.extractedValue
          const m = Moment(refDate, ['YYYY-MM-DD', 'MM/DD/YYYY', 'M/D/YYYY', Moment.ISO_8601], true)
          if (m.isValid()) {
            refDate = m.format('YYYY-MM-DD')
          }
          updatedCase = {
            ...updatedCase,
            referral: { ...(updatedCase.referral || {}), date: refDate },
          }
        } else if (ent.field === 'Claimant Name' || ent.field === 'Client Name') {
          const cleanedName = cleanCombinedVal(ent.extractedValue || '')
            .replace(/^(?:RN|LPN|MD|DO|PT|DPT|DC|NP|PA-?C|CCM|CRC)\b[\s,.-]*/i, '')
            .trim()
          const parts = cleanedName.split(/\s+/).filter(Boolean)
          if (parts.length > 0) {
            updatedCase = {
              ...updatedCase,
              client: {
                ...(updatedCase.client || {}),
                firstName: parts.length > 1 ? parts.slice(0, -1).join(' ') : parts[0],
                lastName: parts.length > 1 ? parts[parts.length - 1] : updatedCase.client?.lastName,
              },
            }
          }
        } else if (ent.field === 'Date of Birth') {
          updatedCase = {
            ...updatedCase,
            client: { ...(updatedCase.client || {}), birthdate: ent.extractedValue },
          }
        } else if (ent.field === 'Claimant Phone' || ent.field === 'Phone Number') {
          updatedCase = {
            ...updatedCase,
            client: { ...(updatedCase.client || {}), phone: cleanCombinedVal(ent.extractedValue) },
          }
        } else if (ent.field === 'Claimant Address') {
          const addrObj = ent.attributes || { line1: ent.extractedValue }
          updatedCase = {
            ...updatedCase,
            client: { ...(updatedCase.client || {}), address: addrObj },
          }
        } else if (ent.field === 'Occupation') {
          occupationExplicitlyUpdated = true
          const cleanedOcc = cleanCombinedVal(ent.extractedValue)
          updatedCase = {
            ...updatedCase,
            client: { ...(updatedCase.client || {}), occupation: cleanedOcc },
            employer: { ...(updatedCase.employer || {}), occupation: cleanedOcc },
          }
        } else if (ent.field === 'Date of Injury') {
          updatedCase = {
            ...updatedCase,
            injury: { ...(updatedCase.injury || {}), date: ent.extractedValue },
          }
        } else if (ent.field === 'Mechanism of Injury') {
          const existingDesc = updatedCase.injury?.descriptions || []
          const moiVal = (ent.extractedValue || '').trim()
          const moiDescText = moiVal.toLowerCase().startsWith('mechanism') ? moiVal : `Mechanism of Injury: ${moiVal}`
          const isDuplicate = existingDesc.some(d => {
            const dVal = (typeof d === 'string' ? d : d.description || d.text || '').trim().toLowerCase()
            return (
              dVal === moiDescText.toLowerCase() ||
              dVal === moiVal.toLowerCase() ||
              (dVal.length >= 10 &&
                moiVal.length >= 10 &&
                (dVal.includes(moiVal.toLowerCase()) || moiVal.toLowerCase().includes(dVal)))
            )
          })
          const newDescriptions =
            isDuplicate || !moiVal
              ? existingDesc
              : [...existingDesc, { description: moiDescText, provider: ent.attributes?.provider || 'Report' }]

          updatedCase = {
            ...updatedCase,
            injury: {
              ...(updatedCase.injury || {}),
              moi: ent.extractedValue,
              descriptions: newDescriptions,
            },
          }
        } else if (ent.field === 'Nature of Injury') {
          const existingDesc = updatedCase.injury?.descriptions || []
          const noiVal = (ent.extractedValue || '').trim()
          const noiDescText = noiVal.toLowerCase().startsWith('nature') ? noiVal : `Nature of Injury: ${noiVal}`
          const isDuplicate = existingDesc.some(d => {
            const dVal = (typeof d === 'string' ? d : d.description || d.text || '').trim().toLowerCase()
            return (
              dVal === noiDescText.toLowerCase() ||
              dVal === noiVal.toLowerCase() ||
              (dVal.length >= 10 &&
                noiVal.length >= 10 &&
                (dVal.includes(noiVal.toLowerCase()) || noiVal.toLowerCase().includes(dVal)))
            )
          })
          const newDescriptions =
            isDuplicate || !noiVal
              ? existingDesc
              : [...existingDesc, { description: noiDescText, provider: ent.attributes?.provider || 'Report' }]

          updatedCase = {
            ...updatedCase,
            injury: {
              ...(updatedCase.injury || {}),
              noi: ent.extractedValue,
              descriptions: newDescriptions,
            },
          }
        } else if (ent.field === 'Injury Description') {
          const existingDesc = updatedCase.injury?.descriptions || []
          const newDescVal = (ent.extractedValue || '').trim().toLowerCase()
          const isDuplicate = existingDesc.some(d => {
            const dVal = (typeof d === 'string' ? d : d.description || d.text || '').trim().toLowerCase()
            return (
              dVal === newDescVal ||
              (dVal.length >= 10 && newDescVal.length >= 10 && (dVal.includes(newDescVal) || newDescVal.includes(dVal)))
            )
          })
          if (!isDuplicate) {
            updatedCase = {
              ...updatedCase,
              injury: {
                ...(updatedCase.injury || {}),
                descriptions: [
                  ...existingDesc,
                  { description: ent.extractedValue, provider: ent.attributes?.provider || 'Report' },
                ],
              },
            }
          }
        } else if (ent.field === 'Employer') {
          const resolvedEmpName =
            ent.isEdited || ent.status === 'edited' ? ent.extractedValue : ent.attributes?.name || ent.extractedValue
          updatedCase = {
            ...updatedCase,
            employer: {
              ...(updatedCase.employer || {}),
              name: cleanCombinedVal(resolvedEmpName),
              address: ent.attributes?.address || updatedCase.employer?.address,
              contact: {
                ...(updatedCase.employer?.contact || {}),
                phone: cleanCombinedVal(ent.attributes?.phone || updatedCase.employer?.contact?.phone),
              },
              occupation: occupationExplicitlyUpdated
                ? updatedCase.employer?.occupation
                : cleanCombinedVal(updatedCase.employer?.occupation || ent.attributes?.occupation),
            },
          }
        } else if (ent.field === 'Date of Hire') {
          const mHire = Moment(ent.extractedValue, ['MM/DD/YYYY', 'YYYY-MM-DD', 'M/D/YYYY', Moment.ISO_8601], true)
          const stdHireDate = mHire.isValid() ? mHire.format('YYYY-MM-DD') : ent.extractedValue
          updatedCase = {
            ...updatedCase,
            employer: {
              ...(updatedCase.employer || {}),
              dateOfHire: stdHireDate,
            },
          }
        } else if (ent.field === 'Insurance Carrier') {
          updatedCase = {
            ...updatedCase,
            insurer: { ...(updatedCase.insurer || {}), name: ent.extractedValue },
            referral: { ...(updatedCase.referral || {}), insuranceCompany: ent.extractedValue },
          }
        } else if (ent.category === 'diagnosis') {
          const existingDiags = updatedCase.diagnosis || []
          const diagVal = ent.extractedValue
          if (diagVal && !existingDiags.some(d => (d.name || d.description || d) === diagVal)) {
            let diagDate = null
            if (ent.attributes?.date) {
              const m = Moment(ent.attributes.date, ['YYYY-MM-DD', 'MM/DD/YYYY', 'M/D/YYYY', Moment.ISO_8601], true)
              if (m.isValid()) diagDate = m.format('YYYY-MM-DD')
            }
            updatedCase = {
              ...updatedCase,
              diagnosis: [
                ...existingDiags,
                {
                  name: diagVal,
                  description: diagVal,
                  code: ent.attributes?.code || null,
                  anatomicalLevel: ent.attributes?.anatomicalLevel || null,
                  category: ent.isPreExisting
                    ? 'Pre-Existing / Incidental'
                    : ent.attributes?.category || (ent.field === 'Primary Diagnosis' ? 'Primary' : 'Secondary'),
                  date: diagDate,
                  physician: ent.attributes?.physician || null,
                },
              ],
            }
          }
        } else if (ent.category === 'test') {
          const existingTests = updatedCase.tests || []
          const testType = ent.attributes?.type || ent.attributes?.modality || ent.field || 'Diagnostic Test'
          let testDate = ent.attributes?.date || null
          if (testDate) {
            const m = Moment(testDate, ['YYYY-MM-DD', 'MM/DD/YYYY', 'M/D/YYYY', Moment.ISO_8601], true)
            testDate = m.isValid() ? m.format('YYYY-MM-DD') : /^\d{4}-\d{2}-\d{2}$/.test(testDate) ? testDate : null
          }
          const testResults = ent.attributes?.results || ent.extractedValue || null
          if (testType) {
            const matchIdx = existingTests.findIndex(
              t => (t.type || t.name || t.modality) === testType && (t.date || null) === testDate,
            )
            if (matchIdx >= 0) {
              const updated = [...existingTests]
              updated[matchIdx] = {
                ...updated[matchIdx],
                type: testType,
                date: testDate || updated[matchIdx].date || null,
                results: testResults,
              }
              updatedCase = { ...updatedCase, tests: updated }
            } else if (
              !existingTests.some(
                t => (t.type || t.name || t.modality) === testType && (t.results || null) === testResults,
              )
            ) {
              updatedCase = {
                ...updatedCase,
                tests: [
                  ...existingTests,
                  {
                    type: testType,
                    date: testDate || null,
                    results: testResults,
                  },
                ],
              }
            }
          }
        } else if (ent.category === 'medication' && ent.field === 'Medication') {
          const existingMeds = updatedCase.medications || []
          const medName = ent.attributes?.name || ent.extractedValue
          if (medName && !existingMeds.some(m => (m.name || m) === medName)) {
            const relatedToInjury = Boolean(ent.attributes?.relatedToInjury)
            const preInjury = Boolean(ent.attributes?.preInjury || ent.attributes?.isPreInjury)
            const discontinued = Boolean(
              ent.attributes?.discontinued || ent.attributes?.isDiscontinued || ent.attributes?.dateDiscontinued,
            )
            updatedCase = {
              ...updatedCase,
              medications: [
                ...existingMeds,
                {
                  name: medName,
                  dosage: ent.attributes?.dosage,
                  frequency: ent.attributes?.frequency,
                  reason: ent.attributes?.reason || '',
                  dateStarted: ent.attributes?.dateStarted || null,
                  dateDiscontinued: ent.attributes?.dateDiscontinued || null,
                  pharmacy: ent.attributes?.pharmacy || '',
                  relatedToInjury,
                  preInjury,
                  isPreInjury: preInjury,
                  discontinued,
                  isDiscontinued: discontinued,
                },
              ],
            }
          }
        } else if (ent.category === 'allergy' || (ent.category === 'medication' && ent.field === 'Allergy')) {
          const existingAllergies = updatedCase.allergies || []
          const algName = ent.attributes?.name || ent.extractedValue
          if (algName && !existingAllergies.some(a => (a.name || a) === algName)) {
            updatedCase = {
              ...updatedCase,
              allergies: [
                ...existingAllergies,
                {
                  name: algName,
                  reaction: ent.attributes?.reaction,
                },
              ],
            }
          }
        } else if (ent.category === 'appointment') {
          const existingAppts = updatedCase.appointments || []
          if (ent.extractedValue) {
            let startVal = ent.attributes?.date || null
            if (startVal && typeof startVal === 'string') {
              const m = Moment(startVal, ['YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss', Moment.ISO_8601], true)
              startVal = m.isValid() ? m.toISOString() : null
            }
            const apptTitle = ent.attributes?.purpose || ent.extractedValue
            if (!existingAppts.some(a => a.title === apptTitle && (a.start || null) === (startVal || null))) {
              updatedCase = {
                ...updatedCase,
                appointments: [
                  ...existingAppts,
                  {
                    title: apptTitle,
                    description: ent.attributes?.purpose || ent.extractedValue,
                    provider: ent.attributes?.provider || null,
                    start: startVal,
                  },
                ],
              }
            }
          }
        } else if (ent.category === 'surgery') {
          const existingSurgeries = updatedCase.surgeries || []
          const existingSurgeriesHosp = updatedCase.surgeriesAndHospitalizations || []
          if (ent.extractedValue) {
            const surgItem = {
              title: ent.attributes?.procedure || ent.extractedValue,
              procedure: ent.attributes?.procedure || ent.extractedValue,
              dateOfSurgery: ent.attributes?.date,
              date: ent.attributes?.date,
              facility: ent.attributes?.facility,
              surgeon: ent.attributes?.surgeon,
              provider: ent.attributes?.facility || ent.attributes?.surgeon,
              sourceTextSnippet: ent.sourceSnippet,
            }
            updatedCase = {
              ...updatedCase,
              surgeries: [...existingSurgeries, surgItem],
              surgeriesAndHospitalizations: [...existingSurgeriesHosp, surgItem],
            }
          }
        } else if (ent.category === 'status') {
          if (ent.field === 'Work Status') {
            const cap = ent.attributes?.workCapacity || ent.attributes?.workStatus || ent.extractedValue
            const retStatus = ent.attributes?.returnStatus || ent.attributes?.restrictions || null
            const effDate = ent.attributes?.effectiveDate || ent.attributes?.date || null
            const provider = ent.attributes?.treatmentProvider || null
            const treatment = ent.attributes?.currentTreatment || null
            const isLightDuty = Boolean(
              ent.attributes?.lightDuty || /light|desk|modified/i.test(`${cap || ''} ${retStatus || ''}`),
            )

            const existingStatus = updatedCase.status || {}
            const existingHistory = Array.isArray(existingStatus.history)
              ? [...existingStatus.history]
              : Array.isArray(updatedCase.status)
              ? [...updatedCase.status]
              : []

            if (
              existingHistory.length === 0 &&
              (existingStatus.workStatus || existingStatus.returnStatus || existingStatus.date)
            ) {
              existingHistory.push({
                dateLastWorked: existingStatus.dateLastWorked || null,
                date: existingStatus.date || null,
                treatmentProvider: existingStatus.treatmentProvider || null,
                currentTreatment: existingStatus.currentTreatment || null,
                workStatus: existingStatus.workStatus || null,
                returnStatus: existingStatus.returnStatus || null,
                lightDuty: Boolean(existingStatus.lightDuty),
              })
            }

            const newEntry = {
              dateLastWorked: null,
              date: effDate,
              treatmentProvider: provider,
              currentTreatment: treatment,
              workStatus: cap,
              returnStatus: retStatus,
              lightDuty: isLightDuty,
            }

            const existingEntryIdx = existingHistory.findIndex(
              h => h && h.date === effDate && (h.workStatus === cap || (!h.workStatus && !cap)),
            )

            let updatedHistory
            if (existingEntryIdx >= 0) {
              updatedHistory = [...existingHistory]
              updatedHistory[existingEntryIdx] = {
                ...updatedHistory[existingEntryIdx],
                ...newEntry,
              }
            } else {
              updatedHistory = [...existingHistory, newEntry]
            }

            updatedCase = {
              ...updatedCase,
              status: {
                ...existingStatus,
                workStatus: cap,
                returnStatus: retStatus || existingStatus.returnStatus,
                date: effDate || existingStatus.date,
                treatmentProvider: provider || existingStatus.treatmentProvider,
                lightDuty: isLightDuty || existingStatus.lightDuty,
                history: updatedHistory,
              },
              workStatus: {
                ...(updatedCase.workStatus || {}),
                status: cap,
                workCapacity: cap,
                restrictions: retStatus,
                effectiveDate: effDate,
              },
            }
          } else if (ent.field === 'MMI / Impairment') {
            const rawReached = ent.attributes?.isMmiReached ?? ent.attributes?.reached
            const isReached =
              rawReached === true || rawReached === 'true'
                ? true
                : rawReached === false || rawReached === 'false'
                ? false
                : null
            const mmiDateStr = ent.attributes?.mmiDate || ent.attributes?.date || ent.extractedValue
            let stdMmiDate = null
            if (mmiDateStr) {
              const m = Moment(mmiDateStr, ['YYYY-MM-DD', 'MM/DD/YYYY', 'M/D/YYYY', Moment.ISO_8601], true)
              if (m.isValid()) stdMmiDate = m.format('YYYY-MM-DD')
              else if (/^\d{4}-\d{2}-\d{2}$/.test(mmiDateStr)) stdMmiDate = mmiDateStr
            }
            const rawRating = ent.attributes?.impairmentRating
            const parsedRating = rawRating ? parseFloat(String(rawRating).replace(/[^0-9.]/g, '')) : null
            let narrative = ent.attributes?.narrative
            if (!narrative) {
              const parts = []
              if (isReached !== null) {
                parts.push(`MMI: ${isReached ? 'Reached' : 'Not Reached'}`)
              } else {
                parts.push('MMI Evaluation')
              }
              if (stdMmiDate) {
                parts.push(`on ${stdMmiDate}`)
              }
              if (Number.isFinite(parsedRating)) {
                parts.push(`• Impairment: ${parsedRating}%`)
              }
              narrative = parts.join(' ')
            }

            const existingMmi = updatedCase.mmi || {}
            const existingHistory = Array.isArray(existingMmi.history) ? [...existingMmi.history] : []

            if (existingHistory.length === 0) {
              const rawExReached =
                typeof existingMmi.reached === 'boolean'
                  ? existingMmi.reached
                  : typeof existingMmi.isMmiReached === 'boolean'
                  ? existingMmi.isMmiReached
                  : null
              const exDate = existingMmi.date || existingMmi.mmiDate || null
              if (exDate || rawExReached !== null || existingMmi.impairmentRating) {
                const exParts = []
                if (rawExReached !== null) {
                  exParts.push(`MMI: ${rawExReached ? 'Reached' : 'Not Reached'}`)
                } else {
                  exParts.push('MMI Evaluation')
                }
                if (exDate) {
                  exParts.push(`on ${exDate}`)
                }
                if (existingMmi.impairmentRating) {
                  exParts.push(`• Impairment: ${existingMmi.impairmentRating}%`)
                }
                existingHistory.push({
                  date: exDate,
                  narrative: exParts.join(' '),
                  isActual: rawExReached === true,
                })
              }
            }

            const newEntry = {
              date: stdMmiDate,
              narrative,
              isActual: isReached === true,
            }

            const existingEntryIdx = existingHistory.findIndex(
              h => h && h.date === stdMmiDate && (h.isActual === isReached || (!h.date && !stdMmiDate)),
            )

            let updatedHistory
            if (existingEntryIdx >= 0) {
              updatedHistory = [...existingHistory]
              updatedHistory[existingEntryIdx] = {
                ...updatedHistory[existingEntryIdx],
                ...newEntry,
              }
            } else {
              updatedHistory = [...existingHistory, newEntry]
            }

            updatedCase = {
              ...updatedCase,
              mmi: {
                ...existingMmi,
                reached: isReached,
                isMmiReached: isReached,
                date: stdMmiDate || existingMmi.date,
                mmiDate: stdMmiDate || existingMmi.mmiDate,
                impairmentRating: Number.isFinite(parsedRating) ? parsedRating : existingMmi.impairmentRating || null,
                history: updatedHistory,
              },
            }
          }
        }
      })

      // Sequentially process and link providers with parent-child organization hierarchy
      updatedCase = await applyAcceptedProviders(acceptedEntities, updatedCase, caseNumber)

      // Clear pending extractions staging container
      delete updatedCase.pendingExtractions
      delete updatedCase.pending_extractions
      delete updatedCase.address

      if (updatedCase.referral?.date) {
        const m = Moment(updatedCase.referral.date, ['YYYY-MM-DD', 'MM/DD/YYYY', 'M/D/YYYY', Moment.ISO_8601], true)
        if (m.isValid()) {
          updatedCase.referral.date = m.format('YYYY-MM-DD')
        }
      }

      if (updatedCase.extensions) {
        const exts = { ...updatedCase.extensions }
        delete exts.pendingExtractions
        delete exts.pending_extractions
        updatedCase.extensions = exts
      }

      // Clean empty date strings to null to satisfy JSON schema validation pattern constraints
      const cleanEmptyDates = obj => {
        if (!obj || typeof obj !== 'object') return
        Object.keys(obj).forEach(key => {
          if (obj[key] && typeof obj[key] === 'object') {
            cleanEmptyDates(obj[key])
          } else if (typeof obj[key] === 'string') {
            const lowerKey = key.toLowerCase()
            const isDateField = lowerKey.includes('date') || lowerKey === 'birthdate' || lowerKey === 'dateofhire'
            const isUtcOrTimestamp = lowerKey.endsWith('utc') || lowerKey.endsWith('timestamp')
            if (isDateField && !isUtcOrTimestamp) {
              const trimmed = obj[key].trim()
              if (trimmed === '' || trimmed.toLowerCase() === 'null') {
                obj[key] = null
              } else {
                const m = Moment(trimmed, ['YYYY-MM-DD', 'MM/DD/YYYY', 'M/D/YYYY', Moment.ISO_8601], true)
                if (m.isValid()) {
                  obj[key] = m.format('YYYY-MM-DD')
                } else if (!/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) {
                  obj[key] = null
                }
              }
            }
          }
        })
      }
      cleanEmptyDates(updatedCase)

      if (Array.isArray(updatedCase.diagnosis)) {
        updatedCase.diagnosis = updatedCase.diagnosis.map(d => {
          if (!d || typeof d !== 'object') return d
          let dDate = d.date
          if (typeof dDate === 'string') {
            const trimmed = dDate.trim()
            if (trimmed === '' || trimmed.toLowerCase() === 'null') {
              dDate = null
            } else {
              const m = Moment(trimmed, ['YYYY-MM-DD', 'MM/DD/YYYY', 'M/D/YYYY', Moment.ISO_8601], true)
              dDate = m.isValid() ? m.format('YYYY-MM-DD') : /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? trimmed : null
            }
          }
          return {
            ...d,
            date: dDate ?? null,
          }
        })
      }

      if (Array.isArray(updatedCase.tests)) {
        updatedCase.tests = updatedCase.tests.map(t => {
          if (!t || typeof t !== 'object') return t
          let tDate = t.date
          if (typeof tDate === 'string') {
            const trimmed = tDate.trim()
            if (trimmed === '' || trimmed.toLowerCase() === 'null') {
              tDate = null
            } else {
              const m = Moment(trimmed, ['YYYY-MM-DD', 'MM/DD/YYYY', 'M/D/YYYY', Moment.ISO_8601], true)
              tDate = m.isValid() ? m.format('YYYY-MM-DD') : /^\d{4}-\d{2}-\d{2}$/.test(trimmed) ? trimmed : null
            }
          }
          return {
            type: t.type || t.modality || t.name || 'Diagnostic Test',
            date: tDate ?? null,
            results: t.results ?? null,
          }
        })
      }

      if (Array.isArray(updatedCase.appointments)) {
        updatedCase.appointments = updatedCase.appointments.map(a => {
          if (!a || typeof a !== 'object') return a
          let startVal = a.start || a.date || null
          if (startVal && typeof startVal === 'string') {
            const m = Moment(startVal, ['YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss', Moment.ISO_8601], true)
            startVal = m.isValid() ? m.toISOString() : null
          }
          let endVal = a.end || null
          if (endVal && typeof endVal === 'string') {
            const m = Moment(endVal, ['YYYY-MM-DD', 'YYYY-MM-DDTHH:mm:ss', Moment.ISO_8601], true)
            endVal = m.isValid() ? m.toISOString() : null
          }
          const cleaned = {
            title: a.title || a.purpose || 'Appointment',
            description: a.description || a.purpose || null,
            attendees: a.attendees || null,
            start: startVal,
            end: endVal,
            provider: a.provider || null,
          }
          if (a.location) cleaned.location = a.location
          if (a.transportationServices) cleaned.transportationServices = a.transportationServices
          if (a.translationServices) cleaned.translationServices = a.translationServices
          if (a.confirmations) cleaned.confirmations = a.confirmations
          return cleaned
        })
      }

      // Explicitly purge staged extractions on the backend so active jobs/DB records are cleared
      try {
        await CasesService.cancelSmartUpload(caseNumber)
      } catch {}

      updatedCase = sanitizeCaseSystemsAndFunctional(updatedCase)
      const newCaseModel = await CasesService.update(updatedCase)
      const cleanModel = { ...newCaseModel }
      delete cleanModel.pendingExtractions
      delete cleanModel.pending_extractions
      if (cleanModel.extensions) {
        cleanModel.extensions = { ...cleanModel.extensions }
        delete cleanModel.extensions.pendingExtractions
        delete cleanModel.extensions.pending_extractions
      }
      setCaseModel(cleanModel)
      setShowHitlModal(false)

      // Clean up URL params if present
      const searchParams = new URLSearchParams(location?.search)
      if (searchParams.has('review') || searchParams.has('from')) {
        searchParams.delete('review')
        searchParams.delete('from')
        const remaining = searchParams.toString()
        history.replace(`${location.pathname}${remaining ? `?${remaining}` : ''}`)
      }

      cogoToast.success('Clinical data confirmed and added to case!', { position: 'top-right' })
      if (refreshCaseStatus) refreshCaseStatus()
      refreshCaseData()
    }

    const handleCancelHitl = async () => {
      try {
        await CasesService.cancelSmartUpload(caseNumber)
        cogoToast.success('Staged extractions discarded.', { position: 'top-right' })
      } catch {}

      setCaseModel(prev => {
        if (!prev) return prev
        const updated = { ...prev }
        delete updated.pendingExtractions
        if (updated.extensions) {
          const exts = { ...updated.extensions }
          delete exts.pendingExtractions
          delete exts.pending_extractions
          updated.extensions = exts
        }
        return updated
      })
      setShowHitlModal(false)
      refreshCaseData()
    }

    const getFormattedDateTimeValue = (startLocal, endLocal, appointment) => {
      if (!startLocal && !appointment?.startUtc) {
        return <p className="text-details mb-0 text-muted font-italic">To be scheduled</p>
      }
      if (appointment?.isAllDay && appointment?.startUtc) {
        const date = Moment.utc(appointment.startUtc).format('dddd, MMMM DD, YYYY')
        return <p className="text-details mb-0">{date}</p>
      }
      if (!!startLocal && !!endLocal) {
        const date = Moment(startLocal).format('dddd, MMMM DD, YYYY')
        const startTime = Moment(startLocal).format('h:mma')
        const endTime = Moment(endLocal).format('h:mma')

        return (
          <p className="text-details mb-0">
            {date} &#8226; {startTime}-{endTime}
          </p>
        )
      } else if (startLocal) {
        const startM = Moment(startLocal)
        const date = startM.format('dddd, MMMM DD, YYYY')
        const startTime = startM.format('h:mma')

        return (
          <p className="text-details mb-0">
            {date} &#8226; {startTime}
          </p>
        )
      } else {
        return <p className="text-details mb-0 text-muted font-italic">To be scheduled</p>
      }
    }

    const convertUtcToLocalDateTime = (utcDateTime, appointment) => {
      if (!utcDateTime) return null
      if (appointment?.isAllDay) {
        return Moment(Moment.utc(utcDateTime).format('YYYY-MM-DD'), 'YYYY-MM-DD').startOf('day').format()
      }
      return Moment.utc(utcDateTime).local().format()
    }

    const handleButtonClick = (path, extraState = {}) => {
      const [pathname, search] = String(path || '').split('?')
      history.push({
        pathname,
        search: search ? `?${search}` : '',
        state: { from: history.location.pathname, ...extraState },
      })
    }

    const orderedIncompleteAppointments = orderBy(incompleteAppointments, x => x.startUtc)
    const orderedAllAppointments = orderBy(
      allAppointments && allAppointments.length ? allAppointments : incompleteAppointments,
      x => x.startUtc,
      'desc',
    )
    const appointments = showAllAppointments ? orderedAllAppointments : orderedIncompleteAppointments
    const displayedAppointments = showAllAppointments ? appointments : appointments.slice(0, 6)
    // When there are few (or zero) appointments, widen the task column so long task
    // names aren't truncated. Threshold ≤ 2 means a lone appointment card doesn't
    // artificially squeeze tasks. Content-driven — no case-type hard-coding needed.
    const hasFewAppointments = displayedAppointments.length <= 2

    const toggleCaseStatus = async () => {
      await CasesService.toggleCaseStatus(caseNumber)
      await refreshMembers()
      refreshCaseStatus()
    }

    const handleCaseClose = () => {
      if (caseModel.caseStatus === 'closed') {
        toggleCaseStatus()
      } else {
        setShowCloseModal(true)
      }
    }

    const confirmCaseClose = async () => {
      setShowCloseModal(false)
      await toggleCaseStatus()
    }

    const saveName = async () => {
      setIsEditing(false)
      if (firstName !== clientFirstName || lastName !== clientLastName) {
        const caseData = { ...caseModel, client: { ...caseModel.client, firstName, lastName } }
        const newCaseModel = await CasesService.update(caseData)
        setCaseModel(newCaseModel)
      }
    }

    const handleInjuryBlur = async e => {
      setIsEditingInjuryDate(false)
      const formattedInjuryDate = formatDate(injuryDate)
      if (formattedInjuryDate !== injuryDateValue) {
        const caseData = { ...caseModel, injury: { ...caseModel.injury, date: formattedInjuryDate } }
        const newCaseModel = await CasesService.update(caseData)
        setCaseModel(newCaseModel)
      }
    }

    const handleBirthDateBlur = async e => {
      setIsEditingBirthdate(false)
      const formattedBirthDate = formatDate(birthDate)
      if (formattedBirthDate !== birthDateValue) {
        const caseData = { ...caseModel, client: { ...caseModel.client, birthdate: formattedBirthDate } }
        const newCaseModel = await CasesService.update(caseData)
        setCaseModel(newCaseModel)
      }
    }

    const handleClaimNumberBlur = async e => {
      setIsEditingClaimNumber(false)
      if (claimNumber !== claimNumberValue) {
        const caseData = {
          ...caseModel,
          referral: {
            ...(caseModel?.referral || {}),
            claimNumber,
          },
        }
        const newCaseModel = await CasesService.update(caseData)
        setCaseModel(newCaseModel)
      }
    }

    const handleEditClaimNumber = () => {
      setClaimNumber(claimNumberValue)
      setIsEditingClaimNumber(true)
    }

    const handEditButtonClick = () => {
      setIsEditing(true)
      inputRef.current.focus()
    }

    const undoDateFormatting = date => {
      if (!date || date === '--') return ''
      const m = Moment(date, ['MM/DD/YYYY', 'YYYY-MM-DD'])
      return m.isValid() ? m.format('YYYY-MM-DD') : ''
    }

    const handleEditInjuryDate = () => {
      const unformattedInjuryDate = undoDateFormatting(injuryDateValue)
      setInjuryDate(unformattedInjuryDate)
      setIsEditingInjuryDate(true)
      inputRef.current.focus()
    }

    const handleEditBirthDate = () => {
      const unformattedBirthDate = undoDateFormatting(birthDateValue)
      setBirthDate(unformattedBirthDate)
      setIsEditingBirthdate(true)
      inputRef.current.focus()
    }

    const isCaseClosed = caseModel.caseStatus === 'closed'

    const handleGenerateReviewerLink = async () => {
      setIsGeneratingLink(true)
      try {
        await CasesService.generateReviewerLink(caseNumber).onSuccess(async data => {
          const reviewUrl = `${window.location.origin}/review/${data.token}`
          try {
            await navigator.clipboard.writeText(reviewUrl)
            setReviewerLinkToast({
              type: 'success',
              message: 'Reviewer link copied to clipboard. Paste it into an email.',
            })
            setTimeout(() => setReviewerLinkToast(null), 6000)
          } catch {
            // Clipboard write blocked by the browser — surface the URL so the user can copy manually.
            setReviewerLinkToast({ type: 'fallback', url: reviewUrl })
            // No auto-dismiss: the user needs time to select and copy.
          }
        })
      } catch {
        setReviewerLinkToast({ type: 'error', message: 'Could not generate reviewer link. Please try again.' })
        setTimeout(() => setReviewerLinkToast(null), 6000)
      } finally {
        setIsGeneratingLink(false)
      }
    }

    const isRule16 = caseModel?.caseTypeKey === 'ColoradoRule16'

    const handleDownloadStatusReport = async () => {
      setIsDownloadingStatusReport(true)
      try {
        await CasesService.downloadStatusReport(caseNumber)
      } catch (err) {
        showError(err.message || 'Failed to generate Status Report.')
      } finally {
        setIsDownloadingStatusReport(false)
      }
    }

    const handleRefreshMetadata = async () => {
      setIsRefreshingMetadata(true)
      try {
        const res = await CasesService.refreshMetadata(caseNumber)
        const targetJobId = res?.jobId ? String(res.jobId).toLowerCase() : null
        cogoToast.info('Case data refresh started in background. Analyzing documents with AI...', {
          position: 'top-right',
          hideAfter: 6,
        })

        // Poll for completion in case notification is delayed
        const pollInterval = 3000
        const maxPollDuration = 90000 // 90 seconds
        const startTime = Date.now()

        const pollTimer = setInterval(async () => {
          if (Date.now() - startTime > maxPollDuration) {
            clearInterval(pollTimer)
            setIsRefreshingMetadata(false)
            return
          }

          try {
            const freshCase = await CasesService.get(caseNumber)
            const p = freshCase?.pendingExtractions || freshCase?.extensions?.pendingExtractions
            const matchesJob = targetJobId ? p?.jobId && String(p.jobId).toLowerCase() === targetJobId : false
            const isFresh = p?.uploadedAt ? new Date(p.uploadedAt).getTime() >= startTime - 2000 : false

            if (p && (matchesJob || isFresh)) {
              clearInterval(pollTimer)
              setCaseModel(freshCase)
              setIsRefreshingMetadata(false)
              setShowHitlModal(true)
              cogoToast.success('Case data refreshed! Staged extractions are ready for review.', {
                position: 'top-right',
              })
            }
          } catch {
            // Ignore transient poll error
          }
        }, pollInterval)
      } catch (err) {
        showError(err.message || 'Failed to refresh case data.')
        setIsRefreshingMetadata(false)
      }
    }

    const handleActivateCase = async () => {
      setIsActivating(true)
      setActivateError(null)
      try {
        await CasesService.completeIntake(caseNumber, [])
          .onSuccess(async () => {
            if (refreshCaseStatus) await refreshCaseStatus()
          })
          .onBadRequest((err, json) => {
            // Extract only the validation messages — strip the 'Request is not valid…' title prefix
            const msg = json?.errors ? Object.values(json.errors).flat().join(' ') : err
            setActivateError(msg)
          })
      } catch {
        setActivateError('An unexpected error occurred while activating the case.')
      } finally {
        setIsActivating(false)
      }
    }

    const canDeleteCase = Permissions.can(effectiveUser, CAPABILITIES.DELETE_CASE)

    const refreshCaseData = async () => {
      try {
        const updatedCase = await CasesService.get(caseNumber)
        setCaseModel(updatedCase)
        const updatedSummary = await CasesService.getSummary(caseNumber)
        setCaseSummaryModel(updatedSummary)
        if (refreshCaseStatus) await refreshCaseStatus()
      } catch (err) {
        showError(err.message || 'Failed to refresh case data.')
      }
    }

    const handleDeleteDraft = async () => {
      setIsDeletingDraft(true)
      try {
        await CasesService.delete(caseNumber)
        history.push('/cases')
      } catch {
        showError('Failed to delete draft case.')
        setIsDeletingDraft(false)
        setShowDeleteDraftConfirm(false)
      }
    }

    return (
      <div className="container overview-page-container pt-5 px-4">
        {isDraft && (
          <div className="alert alert-warning d-flex justify-content-between align-items-center mb-3 px-3 py-2 rounded">
            <div>
              <strong>This case is in Draft.</strong>
              {isRule16 ? (
                <span className="ml-2 text-dark">Activate to begin case management.</span>
              ) : (
                <span className="ml-2 text-dark">
                  Add a Date of Referral and Adjuster in the intake form, then activate to begin case management.
                </span>
              )}
              <div className="text-muted small mt-1">
                <i className="bi bi-info-circle mr-1" />
                Multiple source documents may have duplicate or overlapping elements. Review and reconcile clinical data
                before activating.
              </div>
              {(caseModel?.skipInitialReport || Number(caseModel?.startingProgressReportNumber) > 1) && (
                <div className="mt-1">
                  <span className="badge badge-info px-2 py-1" style={{ fontSize: '0.78rem' }}>
                    <i className="fas fa-history mr-1" />
                    Ongoing Case: Next report is Progress Report #{caseModel.startingProgressReportNumber || 1}
                    {caseModel.startingReportDate
                      ? ` (starting ${Moment.utc(caseModel.startingReportDate).format('MM/DD/YYYY')})`
                      : ''}
                  </span>
                </div>
              )}
              {activateError && <div className="text-danger small mt-1">{activateError}</div>}
            </div>
            <div className="d-flex align-items-center" style={{ gap: '8px', flexShrink: 0 }}>
              {canUpdateCase && (caseModel?.pendingExtractions || caseModel?.extensions?.pendingExtractions) && (
                <button
                  id="smartCaseReviewDraftBtn"
                  type="button"
                  className="btn btn-primary btn-sm font-weight-bold shadow-sm"
                  onClick={() => setShowHitlModal(true)}
                  style={{ whiteSpace: 'nowrap' }}
                  title="Review and reconcile duplicate diagnoses, medications, allergies, and providers extracted from source documents"
                >
                  <i className="bi bi-stars mr-1" />
                  Review Smart Intake Details
                </button>
              )}
              {canDeleteCase && (
                <button
                  className="btn btn-link btn-sm text-danger p-0"
                  style={{ whiteSpace: 'nowrap' }}
                  onClick={() => setShowDeleteDraftConfirm(true)}
                  disabled={isActivating || isDeletingDraft}
                >
                  Delete Draft
                </button>
              )}
              <button
                id="activateCaseBtn"
                className="btn btn-warning btn-sm text-dark font-weight-bold"
                onClick={handleActivateCase}
                disabled={isActivating || isDeletingDraft}
                style={{ whiteSpace: 'nowrap' }}
                title={
                  caseModel.allowedReportTypes?.includes('Initial') && !caseModel.skipInitialReport
                    ? 'Activating will generate the Initial Case Report and begin the case management timeline.'
                    : 'Activating will begin the case management timeline.'
                }
              >
                {isActivating
                  ? 'Activating…'
                  : caseModel.allowedReportTypes?.includes('Initial') && !caseModel.skipInitialReport
                  ? 'Activate Case ↓ Initial Report'
                  : 'Activate Case'}
              </button>
            </div>
          </div>
        )}
        {isCaseClosed
          ? null
          : isRule16 &&
            !isDraft && (
              <div className="pb-3 d-flex align-items-center" style={{ gap: '8px' }}>
                <button
                  id="generateReviewerLinkBtn"
                  className="btn btn-outline-primary btn-sm"
                  onClick={handleGenerateReviewerLink}
                  disabled={isGeneratingLink}
                >
                  <i className="fas fa-copy mr-1" />
                  {isGeneratingLink ? 'Generating…' : 'Copy Reviewer Link'}
                </button>
                {reviewerLinkToast &&
                  (reviewerLinkToast.type === 'fallback' ? (
                    <span className="d-flex align-items-center" style={{ gap: '6px' }}>
                      <small className="text-warning text-nowrap">Clipboard blocked — copy manually:</small>
                      <input
                        type="text"
                        readOnly
                        className="form-control form-control-sm"
                        style={{ width: '260px' }}
                        value={reviewerLinkToast.url}
                        onFocus={e => e.target.select()}
                      />
                    </span>
                  ) : (
                    <small className={reviewerLinkToast.type === 'error' ? 'text-danger' : 'text-success'}>
                      {reviewerLinkToast.message}
                    </small>
                  ))}
                <button
                  id="downloadStatusReportBtn"
                  className="btn btn-outline-primary btn-sm"
                  onClick={handleDownloadStatusReport}
                  disabled={isDownloadingStatusReport}
                >
                  <i className="fas fa-file-pdf mr-1" />
                  {isDownloadingStatusReport ? 'Generating…' : 'Download Status Report'}
                </button>
              </div>
            )}

        <div className="pb-4 d-flex align-items-center" style={{ gap: '8px' }}>
          <CaseCloseButton handleCaseClose={handleCaseClose} isClosed={isCaseClosed} />
          {!isCaseClosed && (
            <>
              <button
                id="refreshCaseMetadataBtn"
                className="btn btn-utility-subtle btn-sm"
                onClick={handleRefreshMetadata}
                disabled={isRefreshingMetadata}
              >
                <i className={`fas fa-sync-alt mr-1 ${isRefreshingMetadata ? 'fa-spin' : ''}`} />
                {isRefreshingMetadata ? 'Refreshing Data…' : 'Refresh Case Data'}
              </button>
              {(caseModel?.pendingExtractions || caseModel?.extensions?.pendingExtractions) && (
                <button
                  id="reviewStagedExtractionsBtn"
                  type="button"
                  className="btn btn-primary btn-sm font-weight-bold"
                  onClick={() => setShowHitlModal(true)}
                  title="Review extracted data waiting for reconciliation"
                >
                  <i className="fas fa-clipboard-check mr-1" />
                  Review Staged Extractions
                </button>
              )}
              {canUpdateCase && (
                <button
                  id="smartCaseReviewBtn"
                  className="btn btn-utility-subtle btn-sm"
                  onClick={() => setShowHygieneModal(true)}
                  title="Scan case details for duplicate diagnoses, brand/generic medications, redundant allergies, and provider credential issues"
                >
                  <i className="bi bi-stars mr-1" />
                  Smart Case Details Review
                </button>
              )}
            </>
          )}
        </div>

        <Modal show={showCloseModal} onHide={() => setShowCloseModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Close Case</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to close this case? This action will automatically generate a final report.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCloseModal(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={confirmCaseClose}>
              Close Case
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showDeleteDraftConfirm} onHide={() => setShowDeleteDraftConfirm(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Delete Draft Case?</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to permanently delete this draft? All data entered for this case will be removed and
            cannot be recovered.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowDeleteDraftConfirm(false)} disabled={isDeletingDraft}>
              Keep Draft
            </Button>
            <Button variant="danger" onClick={handleDeleteDraft} disabled={isDeletingDraft}>
              {isDeletingDraft ? 'Deleting…' : 'Yes, Delete'}
            </Button>
          </Modal.Footer>
        </Modal>

        {caseModel &&
          (() => {
            const rawDocs =
              caseModel.pendingExtractions?.documents || caseModel.extensions?.pendingExtractions?.documents || []
            const token = getToken()
            const displayDocs = rawDocs.map(doc => {
              const isDocPdf = doc.fileType === 'pdf' || doc.fileName?.toLowerCase().endsWith('.pdf')
              const isDocImage =
                /^(image|png|jpe?g)$/i.test(doc.fileType || '') ||
                /\.(png|jpe?g|gif|webp|bmp|tiff?)$/i.test(doc.fileName || '')
              const isDocWord =
                doc.fileType === 'docx' || doc.fileType === 'doc' || /\.(docx|doc)$/i.test(doc.fileName || '')
              const detectedType = isDocPdf ? 'pdf' : isDocImage ? 'image' : isDocWord ? 'docx' : 'txt'
              const resolvedUrl =
                doc.url ||
                ((isDocPdf || isDocImage) && doc.fileName
                  ? `/api/cases/${caseNumber}/sharefile-files/content/${encodeURIComponent(
                      doc.fileName,
                    )}?access_token=${token}`
                  : null)
              return {
                ...doc,
                fileType: doc.fileType || detectedType,
                url: resolvedUrl,
              }
            })
            return (
              <HitlReviewModal
                show={showHitlModal}
                onHide={() => setShowHitlModal(false)}
                documentData={{
                  documents: displayDocs,
                  fileName:
                    displayDocs[0]?.fileName ||
                    caseModel.pendingExtractions?.documents?.[0]?.fileName ||
                    caseModel.extensions?.pendingExtractions?.documents?.[0]?.fileName ||
                    `Case_${caseNumber}_Intake.txt`,
                  fileType:
                    displayDocs[0]?.fileType ||
                    caseModel.pendingExtractions?.documents?.[0]?.fileType ||
                    caseModel.extensions?.pendingExtractions?.documents?.[0]?.fileType ||
                    'txt',
                  textContent:
                    displayDocs[0]?.textContent ||
                    caseModel.pendingExtractions?.rawText ||
                    caseModel.extensions?.pendingExtractions?.rawText ||
                    caseModel.extractedRawText ||
                    '',
                  url: displayDocs[0]?.url || null,
                }}
                rawExtractedData={caseModel.pendingExtractions || caseModel.extensions?.pendingExtractions || caseModel}
                existingCase={caseModel}
                onApplyChanges={handleApplySmartIntakeHitlChanges}
                onCancelAndDiscard={handleCancelHitl}
                user={effectiveUser}
                title="Smart Intake"
                isSmartIntake={true}
              />
            )
          })()}

        <SmartCaseDetailsReviewModal
          isOpen={showHygieneModal}
          toggle={() => setShowHygieneModal(false)}
          caseNumber={caseNumber}
          onSaveSuccess={refreshCaseData}
          user={effectiveUser}
        />

        <div className="row">
          {isEditing ? (
            <form className="row ml-2 mb-2">
              <input
                value={firstName}
                onChange={e => setFirstName(e.target.value)}
                type="text"
                className="form-control w-auto"
              ></input>
              <input
                value={lastName}
                onChange={e => setLastName(e.target.value)}
                type="text"
                className="form-control w-auto ml-2"
              ></input>
              <Button type="submit" className="ml-2" onClick={() => saveName()}>
                Save
              </Button>
            </form>
          ) : (
            <>
              <h4 className="ml-2">{`${clientFirstName} ${clientLastName}`}</h4>
              {!isCaseClosed && (
                <Button
                  type="button"
                  size="sm"
                  className="ml-1 pb-0 mb-2"
                  variant="light"
                  onClick={() => handEditButtonClick()}
                >
                  <FAIcon name="fa-solid fa-pen" />
                </Button>
              )}
            </>
          )}
        </div>
        <div className="row">
          <div className="col-lg-6 col-md-6 mb-4">
            <div className="form-row">
              <div className="col-md-4">
                <div className="form-group">
                  <label className="form-label" htmlFor="dateOfInjury">
                    DATE OF INJURY
                  </label>
                  {!isEditingInjuryDate && !isCaseClosed && (
                    <Button
                      variant="light"
                      type="button"
                      size="sm"
                      className="ml-1 items-center pb-0"
                      onClick={() => handleEditInjuryDate()}
                    >
                      <FAIcon name="fa-solid fa-pen" />
                    </Button>
                  )}
                  <div className="row">
                    {isEditingInjuryDate ? (
                      <input
                        value={injuryDate}
                        onChange={e => setInjuryDate(e.target.value)}
                        onBlur={handleInjuryBlur}
                        type="date"
                        className="form-control w-75 ml-2"
                      ></input>
                    ) : (
                      <h6 className="ml-2" id="dateOfInjury">{`${injuryDateValue}`}</h6>
                    )}
                  </div>
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label className="form-label" htmlFor="dateOfBirth">
                    DATE OF BIRTH
                  </label>
                  {!isEditingBirthdate && !isCaseClosed && (
                    <Button
                      variant="light"
                      type="button"
                      size="sm"
                      className="ml-1 pb-0 items-center"
                      onClick={() => handleEditBirthDate()}
                    >
                      <FAIcon name="thin fa-pen" />
                    </Button>
                  )}
                  {isEditingBirthdate ? (
                    <input
                      type="date"
                      value={birthDate}
                      onChange={e => setBirthDate(e.target.value)}
                      onBlur={handleBirthDateBlur}
                      className="form-control w-75"
                    ></input>
                  ) : (
                    <h6 id="dateOfBirth">{birthDateValue}</h6>
                  )}
                </div>
              </div>
              <div className="col-md-4">
                <div className="form-group">
                  <label className="form-label" htmlFor="claimNumber">
                    CLAIM NUMBER
                  </label>
                  {!isEditingClaimNumber && !isCaseClosed && (
                    <Button
                      variant="light"
                      type="button"
                      size="sm"
                      className="ml-1 pb-0 items-center"
                      onClick={() => handleEditClaimNumber()}
                    >
                      <FAIcon name="thin fa-pen" />
                    </Button>
                  )}
                  {isEditingClaimNumber ? (
                    <input
                      type="text"
                      value={claimNumber}
                      onChange={e => setClaimNumber(e.target.value)}
                      onBlur={handleClaimNumberBlur}
                      className="form-control w-75"
                      // eslint-disable-next-line jsx-a11y/no-autofocus
                      autoFocus
                    ></input>
                  ) : (
                    <h6 id="claimNumber">{claimNumberValue || '--'}</h6>
                  )}
                </div>
              </div>
            </div>
            <Route
              exact
              path={`${match.path}/reports`}
              render={() => (
                <ReportsListPage
                  caseNumber={caseNumber}
                  showError={showError}
                  isCaseClosed={isCaseClosed}
                  isDraft={isDraft}
                  allowedReportTypes={caseModel.allowedReportTypes}
                  settings={settings}
                  user={user}
                />
              )}
            />
            {caseModel.caseTypeName && (
              <div className="form-group">
                <label className="form-label" htmlFor="caseTypeName">
                  CASE TYPE
                </label>
                <h6 id="caseTypeName" className="ml-0">
                  {caseModel.caseTypeName}
                </h6>
              </div>
            )}
            <div className="form-group">
              <div className="d-flex align-items-center mb-1">
                <label className="form-label mb-0" htmlFor="caseAdminNotes">
                  REFERRAL REQUEST
                </label>
                <button
                  type="button"
                  className="btn btn-link p-0 ml-2"
                  onClick={() => {
                    setReferralNotesText(caseModel.detailsForCaseManager || '')
                    setShowReferralModal(true)
                  }}
                  title="Edit Referral Request"
                >
                  <FAIcon name="pen" className="text-primary" style={{ fontSize: '0.85rem' }} />
                </button>
              </div>
              {caseModel.detailsForCaseManager ? (
                <div id="caseAdminNotes" dangerouslySetInnerHTML={{ __html: caseModel.detailsForCaseManager }} />
              ) : (
                <p id="caseAdminNotes">
                  <span className="text-muted font-italic">No referral request notes added.</span>
                </p>
              )}
            </div>
            <Form.Group>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Form.Label className="form-label mb-0">CASE MEMBERS</Form.Label>
                {Permissions.can(effectiveUser, CAPABILITIES.MANAGE_JOBS) && (
                  <Button
                    variant="link"
                    className="p-0"
                    style={{ fontSize: '0.9rem' }}
                    onClick={() => setShowMembersModal(true)}
                    title="users who plays some role in this case, such as Claims Adjuster or Case Manager"
                  >
                    Manage Case Members
                  </Button>
                )}
              </div>
              <ul style={{ paddingInlineStart: 0 }}>
                {caseMembers?.map((cm, idx) => (
                  <li key={`${cm.id}-${idx}`} className="d-flex justify-content-between ">
                    <span>
                      {cm.firstName} {cm.lastName}
                      {cm.role && (
                        <span className="text-muted ml-2" style={{ fontSize: '0.9em' }}>
                          ({ROLE_NAMES.find(rn => rn.key === cm.role)?.label || cm.role})
                        </span>
                      )}
                      {/* Fallback for old data if needed, or if backend sends roles array (which I removed in favor of single role per row) */}
                    </span>
                  </li>
                ))}
              </ul>

              <ManageCaseMembersModal
                show={showMembersModal}
                onHide={() => setShowMembersModal(false)}
                caseNumber={caseNumber}
                currentMembers={caseMembers}
                allUsers={users}
                onUpdateMembers={refreshMembers}
              />
            </Form.Group>
          </div>
          {!isDraft && (
            <div className="col-lg-4 col-md-5 mb-4 offset-lg-2 offset-md-1">
              <div className="d-flex justify-content-between">
                <h6>Next Progress Report</h6>
                <Link className="text-uppercase text-right" to={`/cases/${caseNumber}/reports`}>
                  File Report
                </Link>
              </div>
              <CaseSummary
                caseSummaryModel={caseSummaryModel}
                reportHoursTarget={settings.reportHoursTarget}
                reportDaysTarget={settings.reportDaysTarget}
              />
              {isUserAdmin && (
                <div className="mt-3 text-right">
                  <Button
                    variant="link"
                    className="p-0 text-info"
                    style={{ fontSize: '0.9rem' }}
                    onClick={handleOpenSettings}
                    title="Edit reporting settings"
                  >
                    <i className="fa fa-wrench mr-1" /> Edit Report Settings
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
        <hr className="mt-0 mb-4" />
        <KeyContactsCard
          caseNumber={caseNumber}
          caseModel={caseModel}
          setCaseModel={setCaseModel}
          canEdit={canUpdateCase}
          onActivityLogged={refreshCaseData}
        />
        <div className="dashboard-container container-fluid">
          <div className="row">
            <div
              className={`appointments-overview-container mt-3 mb-4 col-sm-12 ${
                hasFewAppointments ? 'col-lg-4 col-md-4' : 'col-lg-8 col-md-7'
              }`}
            >
              <div className="col-md-12 px-0">
                <div className="d-flex flex-wrap justify-content-between align-items-center mb-3">
                  <div className="d-flex align-items-center my-1">
                    <FAIcon name="calendar-day" className="fa-lg d-inline-block text-primary" />
                    <h5 className="d-inline-block mb-0 ml-3">{`Appointments ${
                      appointments.length !== displayedAppointments.length
                        ? `(${displayedAppointments.length} of ${appointments.length})`
                        : ''
                    }`}</h5>
                  </div>
                  <div className="d-flex align-items-center my-1">
                    <div className="btn-group btn-group-sm mr-2" role="group" aria-label="Filter appointments">
                      <button
                        type="button"
                        className={`btn ${!showAllAppointments ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setShowAllAppointments(false)}
                      >
                        Incomplete ({orderedIncompleteAppointments.length})
                      </button>
                      <button
                        type="button"
                        className={`btn ${showAllAppointments ? 'btn-primary' : 'btn-outline-secondary'}`}
                        onClick={() => setShowAllAppointments(true)}
                      >
                        All ({orderedAllAppointments.length})
                      </button>
                    </div>
                    <Link
                      to={`/cases/${caseNumber}/appointments`}
                      className="btn btn-outline-primary btn-sm"
                      title="Add an appointment"
                    >
                      <FAIcon name="plus" className="mr-1" /> Add
                    </Link>
                  </div>
                </div>
                <div className="row">
                  {!!displayedAppointments && displayedAppointments.length !== 0 ? (
                    displayedAppointments.map((appointment, index) => {
                      return (
                        <AppointmentDashboardCard
                          key={`appointment-card-${appointment.appointmentNumber || index}`}
                          appointment={appointment}
                          getValue={getValue}
                          getFormattedDateTimeValue={getFormattedDateTimeValue}
                          convertUtcToLocalDateTime={convertUtcToLocalDateTime}
                          handleButtonClick={handleButtonClick}
                          singleColumn={hasFewAppointments}
                        />
                      )
                    })
                  ) : (
                    <div className="col-md-12">
                      <Card className="col-12 mb-3 shadow">
                        <Card.Body>
                          <p className="text-center">
                            <strong>
                              {showAllAppointments
                                ? 'No appointments have been recorded for this case'
                                : 'No upcoming or recent appointments to complete'}
                            </strong>
                          </p>
                          {!showAllAppointments && orderedAllAppointments.length > 0 && (
                            <button
                              type="button"
                              className="btn btn-outline-primary btn-block mb-2"
                              onClick={() => setShowAllAppointments(true)}
                            >
                              View All Appointments ({orderedAllAppointments.length})
                            </button>
                          )}
                          <Link className="btn btn-secondary btn-block" to={`/cases/${caseNumber}/appointments`}>
                            Add an Appointment
                          </Link>
                        </Card.Body>
                      </Card>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div
              className={`tasks-overview-container mt-3 mb-4 px-0 col-sm-12 ${
                hasFewAppointments ? 'col-lg-8 col-md-8' : 'col-lg-4 col-md-5'
              }`}
            >
              <div className="mb-3">
                <FAIcon name="calendar-check" type="fas" className="fa-lg d-inline-block text-primary" />
                <h5 className="d-inline-block mb-0 ml-3">Tasks</h5>
              </div>
              <TaskCard
                incompleteTasks={incompleteTasks}
                completeTasks={completeTasks}
                handleButtonClick={handleButtonClick}
                getValue={getValue}
                caseNumber={caseNumber}
                user={user}
                onTaskDeleted={onTaskDeleted}
              />
            </div>
          </div>
        </div>

        <Modal show={showReferralModal} onHide={() => setShowReferralModal(false)} onEntered={handleEntered} size="lg">
          <Modal.Header closeButton>
            <Modal.Title>Referral Request</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form.Group>
              <Form.Label>Referral Request / Admin Notes</Form.Label>
              <TextArea
                className="form-control p-0 border-0"
                areaProps={{
                  value: referralNotesText,
                  onChange: val => setReferralNotesText(val),
                }}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button
              variant="secondary"
              onMouseDown={e => {
                e.preventDefault()
                setShowReferralModal(false)
              }}
              onClick={() => setShowReferralModal(false)}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onMouseDown={handleSaveReferral}
              onClick={handleSaveReferral}
              disabled={isSavingReferral}
            >
              {isSavingReferral ? 'Saving...' : 'Save'}
            </Button>
          </Modal.Footer>
        </Modal>

        <Modal show={showSettingsModal} onHide={() => setShowSettingsModal(false)}>
          <Modal.Header closeButton>
            <Modal.Title>Edit Reporting Settings</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className="text-muted small mb-3">
              Allows administrators to edit the starting parameters for this case&apos;s reports retrospectively (e.g.
              skipping the Initial Report, changing the first Progress Report sequence number, or overriding the start
              date).
            </p>
            <Form onSubmit={handleSaveSettings}>
              <Form.Group className="mb-3">
                <Form.Check
                  type="checkbox"
                  id="skipInitialReportCheck"
                  label="Skip Initial Case Report Auto-Generation"
                  checked={skipInitialReport}
                  onChange={e => setSkipInitialReport(e.target.checked)}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="firstProgressSeqInput">First Progress Report Sequence Number</Form.Label>
                <Form.Control
                  type="number"
                  id="firstProgressSeqInput"
                  min="1"
                  value={firstProgressSeq}
                  onChange={e => setFirstProgressSeq(e.target.value)}
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label htmlFor="firstStartDateInput">First Report Start Date</Form.Label>
                <Form.Control
                  type="date"
                  id="firstStartDateInput"
                  value={firstStartDate}
                  onChange={e => setFirstStartDate(e.target.value)}
                />
              </Form.Group>
            </Form>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowSettingsModal(false)}>
              Cancel
            </Button>
            <Button variant="primary" onClick={handleSaveSettings} disabled={isSavingSettings}>
              {isSavingSettings ? 'Saving...' : 'Save Settings'}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
    )
  },
)

export default OverviewPage
